"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  Eye,
  EyeOff,
  Globe,
  Info,
  Loader2,
  Percent,
  Save,
  Server,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Ticket,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import type { Settings } from "./types";

type Props = Record<string, never>;

export default function SettingsPanel({}: Props) {
  const [settings, setSettings] = React.useState<Settings | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [savingSite, setSavingSite] = React.useState(false);
  const [saving3xui, setSaving3xui] = React.useState(false);
  const [showPass, setShowPass] = React.useState(false);

  // Site form
  const [siteName, setSiteName] = React.useState("");
  const [supportPhone, setSupportPhone] = React.useState("");
  const [heroTitle, setHeroTitle] = React.useState("");
  const [heroSubtitle, setHeroSubtitle] = React.useState("");
  const [commissionPct, setCommissionPct] = React.useState("");
  const [vpnReferralCode, setVpnReferralCode] = React.useState("");
  // 3x-ui form
  const [threeUrl, setThreeUrl] = React.useState("");
  const [threeUser, setThreeUser] = React.useState("");
  const [threePass, setThreePass] = React.useState("");
  const [inboundId, setInboundId] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await fetch("/api/settings", { cache: "no-store" });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "خطا در دریافت تنظیمات");
        if (cancelled) return;
        const s: Settings = data.settings;
        setSettings(s);
        setSiteName(s.siteName ?? "");
        setSupportPhone(s.supportPhone ?? "");
        setHeroTitle(s.heroTitle ?? "");
        setHeroSubtitle(s.heroSubtitle ?? "");
        setCommissionPct(
          s.commissionPct != null ? String(s.commissionPct) : "",
        );
        setVpnReferralCode(s.vpnReferralCode ?? "");
        setThreeUrl(s.threeXuiUrl ?? "");
        setThreeUser(s.threeXuiUser ?? "");
        setThreePass(s.threeXuiPass ?? "");
        setInboundId(s.inboundId != null ? String(s.inboundId) : "");
      } catch (e) {
        const msg = e instanceof Error ? e.message : "خطا در دریافت تنظیمات";
        toast.error(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function saveSite(e: React.FormEvent) {
    e.preventDefault();
    if (!siteName.trim()) {
      toast.error("نام سایت را وارد کنید.");
      return;
    }
    const commission = commissionPct.trim()
      ? Number(commissionPct)
      : null;
    if (commission != null && (!Number.isFinite(commission) || commission < 0 || commission > 100)) {
      toast.error("درصد کمیسیون باید عددی بین ۰ تا ۱۰۰ باشد.");
      return;
    }
    setSavingSite(true);
    try {
      const body = {
        siteName: siteName.trim(),
        supportPhone: supportPhone.trim(),
        heroTitle: heroTitle.trim() || null,
        heroSubtitle: heroSubtitle.trim() || null,
        commissionPct: commission,
        vpnReferralCode: vpnReferralCode.trim() || null,
        threeXuiUrl: settings?.threeXuiUrl ?? "",
        threeXuiUser: settings?.threeXuiUser ?? "",
        threeXuiPass: settings?.threeXuiPass ?? "",
        inboundId: settings?.inboundId ?? 0,
      };
      const res = await fetch("/api/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا در ذخیره تنظیمات");
      setSettings(data.settings);
      toast.success("اطلاعات سایت ذخیره شد.");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در ذخیره تنظیمات";
      toast.error(msg);
    } finally {
      setSavingSite(false);
    }
  }

  async function save3xui(e: React.FormEvent) {
    e.preventDefault();
    setSaving3xui(true);
    try {
      const body = {
        siteName: settings?.siteName ?? "",
        supportPhone: settings?.supportPhone ?? "",
        heroTitle: settings?.heroTitle ?? null,
        heroSubtitle: settings?.heroSubtitle ?? null,
        commissionPct: settings?.commissionPct ?? null,
        vpnReferralCode: settings?.vpnReferralCode ?? null,
        threeXuiUrl: threeUrl.trim(),
        threeXuiUser: threeUser.trim(),
        threeXuiPass: threePass,
        inboundId: inboundId ? Number(inboundId) : 0,
      };
      const res = await fetch("/api/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا در ذخیره تنظیمات 3x-ui");
      setSettings(data.settings);
      toast.success("اطلاعات اتصال به 3x-ui ذخیره شد.");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در ذخیره تنظیمات 3x-ui";
      toast.error(msg);
    } finally {
      setSaving3xui(false);
    }
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-80 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Site info + hero + commission */}
      <Card className="rounded-2xl border-border/60 shadow-sm">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="flex size-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <Globe className="size-4" />
            </span>
            اطلاعات سایت و صفحه اصلی
          </CardTitle>
          <CardDescription>
            نام سایت، راه ارتباطی، عنوان صفحه اصلی و درصد کمیسیون.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveSite} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-site-name">نام سایت</Label>
              <Input
                id="settings-site-name"
                placeholder="آکادمی فَران"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                disabled={savingSite}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-support">تلفن پشتیبانی</Label>
              <div className="relative">
                <Smartphone className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="settings-support"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="09120000000"
                  value={supportPhone}
                  onChange={(e) =>
                    setSupportPhone(e.target.value.replace(/[^\d]/g, ""))
                  }
                  disabled={savingSite}
                  className="pr-10 text-left"
                />
              </div>
            </div>

            <Separator />

            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-hero-title" className="flex items-center gap-2">
                <Sparkles className="size-4 text-primary" />
                عنوان صفحه اصلی (Hero)
              </Label>
              <Input
                id="settings-hero-title"
                placeholder="مثلاً: آکادمی فَران"
                value={heroTitle}
                onChange={(e) => setHeroTitle(e.target.value)}
                disabled={savingSite}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-hero-sub">زیرعنوان صفحه اصلی</Label>
              <Textarea
                id="settings-hero-sub"
                placeholder="توضیح کوتاه زیر عنوان اصلی..."
                value={heroSubtitle}
                onChange={(e) => setHeroSubtitle(e.target.value)}
                disabled={savingSite}
                className="min-h-16 text-sm"
              />
            </div>

            <Separator />

            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-commission" className="flex items-center gap-2">
                <Percent className="size-4 text-primary" />
                درصد کمیسیون (۰ تا ۱۰۰)
              </Label>
              <div className="relative">
                <Input
                  id="settings-commission"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="10"
                  value={commissionPct}
                  onChange={(e) =>
                    setCommissionPct(e.target.value.replace(/[^\d]/g, ""))
                  }
                  disabled={savingSite}
                  className="pl-9 text-left"
                />
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  درصد
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                درصدی از مبلغ هر سفارش یا خرید به کاربر دعوت‌کننده تعلق می‌گیرد.
              </p>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={savingSite}
                className="bg-primary hover:bg-primary/90"
              >
                {savingSite ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    در حال ذخیره...
                  </>
                ) : (
                  <>
                    <Save className="size-4" />
                    ذخیره
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* VPN referral code */}
      <Card className="rounded-2xl border-primary/40 shadow-sm">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="flex size-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <Ticket className="size-4" />
            </span>
            کد معرف ویژه (دسترسی شبکه خصوصی)
          </CardTitle>
          <CardDescription>
            کد دعوت اختصاصی برای دسترسی به پنل شبکه خصوصی.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveSite} className="flex flex-col gap-4">
            <div className="mb-1 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs leading-relaxed text-muted-foreground">
              <Info className="mt-0.5 size-4 shrink-0 text-primary" />
              <span>
                کاربرانی که با این کد ثبت‌نام کنند، به پنل شبکه خصوصی دسترسی
                پیدا می‌کنند.
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-vpn-ref">کد معرف</Label>
              <Input
                id="settings-vpn-ref"
                dir="ltr"
                placeholder="VPN1403"
                value={vpnReferralCode}
                onChange={(e) =>
                  setVpnReferralCode(e.target.value.toUpperCase())
                }
                disabled={savingSite}
                className="text-left font-mono uppercase"
                maxLength={32}
              />
              <p className="text-xs text-muted-foreground">
                در صورت خالی بودن، هیچ کاربری به‌طور خودکار دسترسی خصوصی دریافت
                نخواهد کرد.
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground">
              <ShieldCheck className="size-4 text-primary" />
              این کد فقط در پنل مدیریت قابل مشاهده است.
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={savingSite}
                className="bg-primary hover:bg-primary/90"
              >
                {savingSite ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    در حال ذخیره...
                  </>
                ) : (
                  <>
                    <Save className="size-4" />
                    ذخیره کد
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* 3x-ui */}
      <Card className="rounded-2xl border-border/60 shadow-sm lg:col-span-2">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="flex size-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <Server className="size-4" />
            </span>
            اتصال به سرور 3x-ui
          </CardTitle>
          <CardDescription>
            تنظیمات پنل 3x-ui برای ساخت خودکار کانفیگ.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs leading-relaxed text-muted-foreground">
            <Info className="mt-0.5 size-4 shrink-0 text-primary" />
            <span>
              با وارد کردن این اطلاعات، هنگام تأیید پرداخت، کلاینت به‌صورت خودکار در
              پنل 3x-ui ساخته می‌شود و لینک اشتراک برای کاربر نمایش داده می‌شود.
            </span>
          </div>
          <form onSubmit={save3xui} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-3x-url">آدرس پنل 3x-ui</Label>
              <div className="relative">
                <Globe className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="settings-3x-url"
                  dir="ltr"
                  placeholder="https://panel.example.com:2053"
                  value={threeUrl}
                  onChange={(e) => setThreeUrl(e.target.value)}
                  disabled={saving3xui}
                  className="pr-10 text-left font-mono text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="settings-3x-user">نام کاربری</Label>
                <Input
                  id="settings-3x-user"
                  dir="ltr"
                  placeholder="admin"
                  value={threeUser}
                  onChange={(e) => setThreeUser(e.target.value)}
                  disabled={saving3xui}
                  className="text-left"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="settings-3x-pass">رمز عبور</Label>
                <div className="relative">
                  <Input
                    id="settings-3x-pass"
                    type={showPass ? "text" : "password"}
                    dir="ltr"
                    placeholder="••••••••"
                    value={threePass}
                    onChange={(e) => setThreePass(e.target.value)}
                    disabled={saving3xui}
                    className="pl-10 text-left"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={showPass ? "پنهان کردن رمز" : "نمایش رمز"}
                    className="absolute left-1 top-1/2 size-8 -translate-y-1/2 text-muted-foreground hover:bg-transparent hover:text-foreground"
                    onClick={() => setShowPass((s) => !s)}
                    tabIndex={-1}
                    disabled={saving3xui}
                  >
                    {showPass ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-inbound">شناسه اینباند (Inbound ID)</Label>
              <Input
                id="settings-inbound"
                dir="ltr"
                inputMode="numeric"
                placeholder="1"
                value={inboundId}
                onChange={(e) =>
                  setInboundId(e.target.value.replace(/[^\d]/g, ""))
                }
                disabled={saving3xui}
                className="text-left"
              />
              <p className="text-xs text-muted-foreground">
                شناسه اینباند VLESS در پنل 3x-ui که کانفیگ‌ها زیر آن ساخته می‌شوند.
              </p>
            </div>

            <Separator />

            <div className="flex items-center gap-2 rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground">
              <ShieldCheck className="size-4 text-primary" />
              رمز عبور به‌صورت امن در سرور ذخیره می‌شود.
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={saving3xui}
                className="bg-primary hover:bg-primary/90"
              >
                {saving3xui ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    در حال ذخیره...
                  </>
                ) : (
                  <>
                    <Save className="size-4" />
                    ذخیره اتصال
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
